/**
 * 🏷 SCHEMA AJANI — yapılandırılmış veri denetimi ve üretimi.
 *
 * İKİ KATMANI AYRI GÖSTERİR, bu kasıtlı:
 *  • schema.org sözlüğü (919 tip): tip/özellik gerçekten var mı → "geçersiz" hatası
 *  • Google zengin sonuç kuralları: zorunlu alan tam mı → "zengin sonuç alınmaz"
 * schema.org'a uygun ama Google'a göre eksik schema çok yaygın; kullanıcının
 * aksiyon alacağı bilgi ikincisidir, o yüzden ayrı ayrı raporlanır.
 */
import { useState } from "react";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ScanLine, Sparkles, Copy, Check, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { SchemaUretici } from "@/components/schema-uretici";
import { SchemaAgaclari, SchemaHamBloklar, type TipKurali } from "@/components/schema-agaci";

type Bulgu = { seviye: "hata" | "uyari" | "bilgi"; mesaj: string; tip?: string; alan?: string };
type Sayfa = {
  url: string; fetchEdildi: boolean; hataMesaji?: string; hamJsonLd?: string[];
  bloklar: { tip: string; gecerliTip: boolean; alanSayisi: number; kaynak?: "json-ld" | "microdata" }[];
  bulgular: Bulgu[]; zenginSonucHazir: string[]; onerilenTipler: string[]; puan: number;
};
type Veri = {
  taranArSayfa: number; ulasilamayan: number; ortalamaPuan: number; schemasiz: number;
  hataliSayfa: number; zenginSonucHazirSayfa: number;
  tipDagilimi: { tip: string; sayfa: number; gecerli: boolean }[];
  enSikSorunlar: { mesaj: string; sayfa: number; seviye: Bulgu["seviye"] }[];
  sayfalar: Sayfa[];
};
type Yanit = {
  generatedAt: string | null; data: Veri | null; running?: boolean; runError?: string | null;
  sozluk?: { tip: number; ozellikliTip: number };
  destekliTipler?: TipKurali[];
};

const SEVIYE: Record<Bulgu["seviye"], { etiket: string; sinif: string }> = {
  hata:  { etiket: "HATA",  sinif: "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400" },
  uyari: { etiket: "UYARI", sinif: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400" },
  bilgi: { etiket: "BİLGİ", sinif: "bg-accent text-muted-foreground" },
};
const puanRengi = (p: number) => (p >= 80 ? "text-success-600 dark:text-success-400" : p >= 50 ? "text-warning-700 dark:text-warning-400" : "text-error-600 dark:text-error-400");
const kisaUrl = (u: string) => u.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "") || "/";
// Google Zengin Sonuç Testi URL parametresiyle acilir — kullanici adresi elle
// yapistirmasin diye dogrudan o sayfaya yollariz.
const richResultsBaglantisi = (url: string) => `https://search.google.com/test/rich-results?url=${encodeURIComponent(url)}`;

/**
 * Sayfada bulunan schema — DOĞRUDAN AÇIK, iki görünüm arasında sekmeyle geçilir.
 * "Detaylı": alan alan ağaç, her alanın yanında zorunlu/önerilen/ekstra rozeti,
 *            eksik alanlar altta listelenir.
 * "Ham kod": her blok kendi tip başlığı altında, birebir JSON-LD.
 * Katlanmıyor: kullanıcı schema'yı görmek için taradı, bir tık daha istememeli.
 */
function BulunanSchema({ kodlar, kurallar }: { kodlar: string[]; kurallar: TipKurali[] }) {
  const [gorunum, setGorunum] = useState<"detay" | "ham">("detay");
  const [kopya, setKopya] = useState(false);
  if (!kodlar.length) return null;
  const hepsi = kodlar.join("\n\n");
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 flex-wrap mb-1.5">
        <span className="text-[11px] font-medium text-muted-foreground">
          Bulunan schema ({kodlar.length} blok)
        </span>
        <div className="flex rounded-lg border border-border overflow-hidden">
          {([["detay", "Detaylı"], ["ham", "Ham kod"]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setGorunum(k)}
              className={`px-2.5 py-0.5 text-[11px] ${gorunum === k ? "bg-accent font-medium" : "text-muted-foreground"}`}>{l}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="h-6 text-[11px] px-2"
          onClick={() => { void navigator.clipboard.writeText(hepsi); setKopya(true); setTimeout(() => setKopya(false), 2000); }}>
          {kopya ? <><Check className="w-3 h-3 mr-1" /> Kopyalandı</> : <><Copy className="w-3 h-3 mr-1" /> Kopyala</>}
        </Button>
      </div>
      {gorunum === "detay"
        ? <SchemaAgaclari hamKodlar={kodlar} kurallar={kurallar} />
        : <SchemaHamBloklar hamKodlar={kodlar} />}
    </div>
  );
}

export default function SchemaAgent() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const qc = useQueryClient();
  // Denetim ve üretici AYRI işler: biri "sitede ne var/ne eksik", diğeri
  // "elimdeki bilgiden schema kur". Sekmeyle ayrıldı ki sayfa dağılmasın.
  const [sekme, setSekme] = useState<"denetim" | "uretici">("denetim");
  const [acik, setAcik] = useState<string | null>(null);
  const [tarananUrl, setTarananUrl] = useState("");
  const [canliSonuc, setCanliSonuc] = useState<Sayfa | null>(null);
  const [canliBusy, setCanliBusy] = useState(false);
  const [canliHata, setCanliHata] = useState<string | null>(null);
  const [uretim, setUretim] = useState<{ url: string; jsonLd: string | null; denetim: Bulgu[]; hata?: string } | null>(null);
  const [uretimBusy, setUretimBusy] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);

  const { data: yanit } = useQuery({
    queryKey: ["schema-audit", activeDomainId],
    enabled: !!activeDomainId,
    refetchInterval: (q) => ((q.state.data as Yanit | undefined)?.running ? 4000 : false),
    queryFn: async (): Promise<Yanit> => {
      try { const r = await fetch(`/api/domains/${activeDomainId}/schema-audit`); return r.ok ? await r.json() : { generatedAt: null, data: null }; }
      catch { return { generatedAt: null, data: null }; }
    },
  });
  const d = yanit?.data ?? null;
  const calisiyor = !!yanit?.running;

  const denetimBaslat = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/schema-audit`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "Başlatılamadı");
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schema-audit", activeDomainId] }),
  });

  const canliTara = async () => {
    if (!activeDomainId || !tarananUrl.trim()) return;
    setCanliBusy(true); setCanliHata(null); setCanliSonuc(null);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/schema-scan`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ url: tarananUrl.trim() }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Taranamadı");
      setCanliSonuc(j.sonuc);
    } catch (e) { setCanliHata((e as Error).message); } finally { setCanliBusy(false); }
  };

  const schemaUret = async (url: string, tip: string) => {
    if (!activeDomainId) return;
    setUretimBusy(true); setUretim(null); setKopyalandi(false);
    try {
      const r = await fetch(`/api/domains/${activeDomainId}/schema-generate`, {
        method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ url, tip }),
      });
      const j = await r.json();
      setUretim({ url, jsonLd: j.jsonLd ?? null, denetim: j.denetim ?? [], hata: j.hata ?? (r.ok ? undefined : j.error) });
    } catch (e) { setUretim({ url, jsonLd: null, denetim: [], hata: (e as Error).message }); } finally { setUretimBusy(false); }
  };

  const SayfaKarti = ({ s }: { s: Sayfa }) => (
    <div className="border-t border-border py-2.5">
      <button onClick={() => setAcik(acik === s.url ? null : s.url)} className="w-full flex items-center gap-3 text-left">
        {acik === s.url ? <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />}
        <span className={`text-sm font-bold tabular-nums w-10 shrink-0 ${puanRengi(s.puan)}`}>{s.fetchEdildi ? s.puan : "—"}</span>
        <span className="text-sm truncate flex-1">{kisaUrl(s.url)}</span>
        {!s.fetchEdildi && <span className="text-[11px] text-muted-foreground shrink-0">{s.hataMesaji}</span>}
        {s.fetchEdildi && (
          <span className="text-[11px] text-muted-foreground shrink-0 hidden sm:inline">
            {s.bloklar.map((b) => `${b.tip}${b.kaynak === "microdata" ? " (microdata)" : ""}`).join(", ") || "schema yok"}
          </span>
        )}
      </button>
      {acik === s.url && s.fetchEdildi && (
        <div className="pl-11 pr-2 pt-2 space-y-1.5">
          {s.zenginSonucHazir.length > 0 && (
            <p className="text-[12px] text-success-700 dark:text-success-400">
              Zengin sonuca hazır: {s.zenginSonucHazir.join(", ")}
            </p>
          )}
          {s.bulgular.map((b, i) => (
            <div key={i} className="flex items-start gap-2 text-[12px]">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${SEVIYE[b.seviye].sinif}`}>{SEVIYE[b.seviye].etiket}</span>
              <span className="text-muted-foreground">{b.mesaj}</span>
            </div>
          ))}
          <BulunanSchema kodlar={s.hamJsonLd ?? []} kurallar={yanit?.destekliTipler ?? []} />
          <div className="flex items-center gap-2 pt-1.5 flex-wrap">
            <a href={s.url} target="_blank" rel="noreferrer" className="text-[11px] underline text-muted-foreground inline-flex items-center gap-1">
              sayfayı aç <ExternalLink className="w-3 h-3" />
            </a>
            <a href={richResultsBaglantisi(s.url)} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="h-6 text-[11px] px-2">
                <ExternalLink className="w-3 h-3 mr-1" /> Rich Results ile test et
              </Button>
            </a>
            {[...s.onerilenTipler, ...s.bloklar.map((b) => b.tip)].slice(0, 4).map((t) => (
              <Button key={t} size="sm" variant="outline" className="h-6 text-[11px] px-2" disabled={uretimBusy}
                onClick={() => schemaUret(s.url, t)}>
                <Sparkles className="w-3 h-3 mr-1" /> {t} üret
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 py-6 w-full max-w-(--breakpoint-2xl) mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">🏷 Schema Ajanı</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Yapılandırılmış veriyi iki katmanda denetler: schema.org sözlüğü
            {yanit?.sozluk ? ` (${yanit.sozluk.tip} tip)` : ""} ve Google zengin sonuç kuralları
            {yanit?.destekliTipler ? ` (${yanit.destekliTipler.length} tip)` : ""}.
          </p>
        </div>
        <Button size="sm" onClick={() => denetimBaslat.mutate()} disabled={calisiyor || denetimBaslat.isPending || !activeDomainId}>
          {calisiyor ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Denetleniyor…</> : "Site Denetimini Başlat"}
        </Button>
      </div>

      <div className="flex gap-1 border-b border-border" role="tablist">
        {([["denetim", "Site Denetimi"], ["uretici", "Schema Üretici"]] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={sekme === k} onClick={() => setSekme(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              sekme === k ? "border-brand-500 text-brand-600 dark:text-brand-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      {sekme === "uretici" && <SchemaUretici />}

      {sekme === "denetim" && (<>
      {denetimBaslat.isError && <Card className="border-error-500/40"><CardContent className="p-3 text-sm text-error-600 dark:text-error-400">{(denetimBaslat.error as Error).message}</CardContent></Card>}
      {yanit?.runError && <Card className="border-error-500/40"><CardContent className="p-3 text-sm text-error-600 dark:text-error-400">{yanit.runError}</CardContent></Card>}

      {/* ── CANLI TEK SAYFA TARAMASI ── */}
      <Card><CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <ScanLine className="w-4 h-4 text-brand-500" />
          <span className="font-semibold text-sm">Canlı Sayfa Taraması</span>
          <span className="text-[11px] text-muted-foreground">— bir sayfayı düzelttikten sonra anında doğrula</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input value={tarananUrl} onChange={(e) => setTarananUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void canliTara(); }}
            placeholder="https://siteniz.com/sayfa"
            className="flex-1 min-w-[240px] text-sm px-3 py-2 rounded-lg border bg-background" />
          <Button size="sm" onClick={() => void canliTara()} disabled={canliBusy || tarananUrl.trim().length < 8}>
            {canliBusy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Taranıyor…</> : "Tara"}
          </Button>
        </div>
        {canliHata && <p className="text-[12px] text-error-600 dark:text-error-400 mt-2">{canliHata}</p>}
        {canliSonuc && (
          <div className="mt-3 rounded-lg border border-border p-3">
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-2xl font-bold tabular-nums ${puanRengi(canliSonuc.puan)}`}>{canliSonuc.fetchEdildi ? canliSonuc.puan : "—"}</span>
              <div className="text-[12px] text-muted-foreground">
                <div>{kisaUrl(canliSonuc.url)}</div>
                <div>{canliSonuc.fetchEdildi ? (canliSonuc.bloklar.map((b) => `${b.tip}${b.kaynak === "microdata" ? " (microdata)" : ""}`).join(", ") || "schema yok") : canliSonuc.hataMesaji}</div>
              </div>
            </div>
            {canliSonuc.bulgular.map((b, i) => (
              <div key={i} className="flex items-start gap-2 text-[12px] py-0.5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${SEVIYE[b.seviye].sinif}`}>{SEVIYE[b.seviye].etiket}</span>
                <span className="text-muted-foreground">{b.mesaj}</span>
              </div>
            ))}
            <BulunanSchema kodlar={canliSonuc.hamJsonLd ?? []} kurallar={yanit?.destekliTipler ?? []} />
            <div className="mt-2">
              <a href={richResultsBaglantisi(canliSonuc.url)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-7 text-[11px]">
                  <ExternalLink className="w-3.5 h-3.5 mr-1" /> Google Rich Results ile test et
                </Button>
              </a>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* ── ÜRETİLEN SCHEMA ── */}
      {(uretimBusy || uretim) && (
        <Card className="border-brand-500/40"><CardContent className="p-4">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <span className="font-semibold text-sm">✨ Üretilen Schema {uretim ? `— ${kisaUrl(uretim.url)}` : ""}</span>
            {uretim?.jsonLd && (
              <Button size="sm" variant="outline" onClick={() => { void navigator.clipboard.writeText(`<script type="application/ld+json">\n${uretim.jsonLd}\n</script>`); setKopyalandi(true); }}>
                {kopyalandi ? <><Check className="w-3.5 h-3.5 mr-1" /> Kopyalandı</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Script olarak kopyala</>}
              </Button>
            )}
          </div>
          {uretimBusy && <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin mr-1.5" /> Üretiliyor…</p>}
          {uretim?.hata && <p className="text-[12px] text-error-600 dark:text-error-400">{uretim.hata}</p>}
          {uretim?.jsonLd && (
            <pre className="text-[11px] bg-accent rounded-lg p-3 overflow-x-auto max-h-80">{uretim.jsonLd}</pre>
          )}
          {(uretim?.denetim.length ?? 0) > 0 && (
            <div className="mt-2 space-y-1">
              {uretim!.denetim.map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px]">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${SEVIYE[b.seviye].sinif}`}>{SEVIYE[b.seviye].etiket}</span>
                  <span className="text-muted-foreground">{b.mesaj}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent></Card>
      )}

      {!d && !calisiyor && (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
          Henüz site denetimi yapılmadı. "Site Denetimini Başlat" ile tüm sayfaların schema'sı taranır.
        </CardContent></Card>
      )}

      {d && (<>
        {/* ── ÖZET ── */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { e: "Ortalama Puan", v: `${d.ortalamaPuan}`, r: puanRengi(d.ortalamaPuan) },
            { e: "Taranan Sayfa", v: `${d.taranArSayfa - d.ulasilamayan}/${d.taranArSayfa}` },
            { e: "Schema'sı Yok", v: `${d.schemasiz}`, r: d.schemasiz ? "text-error-600 dark:text-error-400" : undefined },
            { e: "Hatalı Sayfa", v: `${d.hataliSayfa}`, r: d.hataliSayfa ? "text-error-600 dark:text-error-400" : undefined },
            { e: "Zengin Sonuca Hazır", v: `${d.zenginSonucHazirSayfa}`, r: "text-success-600 dark:text-success-400" },
          ].map((k) => (
            <Card key={k.e}><CardContent className="p-3">
              <div className={`text-2xl font-bold tabular-nums ${k.r ?? ""}`}>{k.v}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{k.e}</div>
            </CardContent></Card>
          ))}
        </div>

        {/* ── TİP DAĞILIMI ── */}
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">Sitede Kullanılan Schema Tipleri</div>
          {d.tipDagilimi.length === 0 && <p className="text-sm text-muted-foreground">Hiçbir sayfada schema bulunamadı.</p>}
          <div className="flex flex-wrap gap-2">
            {d.tipDagilimi.map((t) => (
              <span key={t.tip} className={`text-[12px] px-2.5 py-1 rounded-full ${t.gecerli ? "bg-accent" : "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400"}`}>
                {t.tip} <b className="tabular-nums">{t.sayfa}</b>{t.gecerli ? "" : " · geçersiz tip"}
              </span>
            ))}
          </div>
        </CardContent></Card>

        {/* ── EN SIK SORUNLAR ── */}
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-2">En Sık Sorunlar</div>
          <div className="space-y-1.5">
            {d.enSikSorunlar.map((p, i) => (
              <div key={i} className="flex items-start gap-2 text-[12px]">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${SEVIYE[p.seviye].sinif}`}>{SEVIYE[p.seviye].etiket}</span>
                <span className="tabular-nums text-muted-foreground shrink-0 w-12">{p.sayfa} sayfa</span>
                <span className="text-muted-foreground">{p.mesaj}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>

        {/* ── SAYFA LİSTESİ ── */}
        <Card><CardContent className="p-4">
          <div className="font-semibold text-sm mb-1">Sayfalar</div>
          <p className="text-[11px] text-muted-foreground mb-1">Bir satıra tıklayarak bulguları aç; oradan o sayfa için schema ürettirebilirsin.</p>
          {[...d.sayfalar].sort((a, b) => a.puan - b.puan).map((s) => <SayfaKarti key={s.url} s={s} />)}
        </CardContent></Card>
      </>)}
      </>)}
    </div>
  );
}
